@file:Suppress("unused")

package com.siriusxm.cmc.charts

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement

/**
 * Generic Chart component that accepts a Chart.js configuration object.
 * This provides full access to all Chart.js functionality.
 *
 * @param configuration The Chart.js configuration object
 * @param height The height of the chart canvas (default: 300px)
 * @param key Optional key to control when chart should be recreated vs updated
 */
@Composable
public fun ChartJs(
    configuration: ChartConfiguration,
    height: CSSSizeValue<*>? = null,
    key: Any? = null
) {
    var chart: Chart.Chart? by remember { mutableStateOf(null) }
    var canvasEl by remember { mutableStateOf<HTMLCanvasElement?>(null) }

    // Register Chart.js components once
    LaunchedEffect(Unit) {
        Chart.register(
            ChartComponents.LineController,
            ChartComponents.LineElement,
            ChartComponents.PointElement,
            ChartComponents.BarController,
            ChartComponents.BarElement,
            ChartComponents.CategoryScale,
            ChartComponents.TimeScale,
            ChartComponents.LinearScale,
            ChartComponents.Legend,
            ChartComponents.Tooltip,
            ChartComponents.Title,
        )
    }

    // Create chart when key changes or on first run
    DisposableEffect(key, canvasEl) {
        val c = canvasEl
        if (c != null) {
            val ctx = c.getContext("2d") as CanvasRenderingContext2D
            chart?.destroy()
            chart = Chart.Chart(ctx, configuration)
        }

        onDispose {
            chart?.destroy()
            chart = null
        }
    }

    // Update chart data when configuration changes (but key stays the same)
    LaunchedEffect(configuration) {
        chart?.let { chartInstance ->
            // Update chart data and options
            chartInstance.asDynamic().data = configuration.data
            chartInstance.asDynamic().options = configuration.options
            chartInstance.update()
        }
    }

    Div({
        style {
            overflow("hidden")
        }
    }) {
        if (height == null) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    marginBottom(15.px)
                    position(Position.Relative)
                    top((-15).px)
                }
            })
        }
        Canvas({
            ref {
                canvasEl = it
                onDispose {
                    if (canvasEl === it) canvasEl = null
                }
            }
            style {
                height?.let {
                    height(it)
                }
            }
        })
    }
}


@JsModule("chart.js/auto")
@JsNonModule
public external object Chart {
    public fun register(vararg components: dynamic)

    public class Chart(ctx: dynamic, config: ChartConfiguration) {
        public fun update()
        public fun destroy()
    }
}

@JsModule("chart.js")
@JsNonModule
public external object ChartComponents {
    public val LineController: dynamic
    public val LineElement: dynamic
    public val PointElement: dynamic
    public val BarController: dynamic
    public val BarElement: dynamic
    public val CategoryScale: dynamic
    public val TimeScale: dynamic
    public val LinearScale: dynamic
    public val Legend: dynamic
    public val Tooltip: dynamic
    public val Title: dynamic
}

@JsModule("chartjs-adapter-date-fns")
@JsNonModule
public external object ChartjsAdapterDateFns

public external interface ChartConfiguration {
    public var type: String
    public var data: ChartData
    public var options: ChartOptions?
}

public external interface ChartData {
    public var labels: Array<String>?
    public var datasets: Array<Dataset>
}

public external interface Dataset {
    public var label: String?
    public var data: Array<*>? // Can be numbers or DatasetPoint objects
    public var borderColor: String?
    public var backgroundColor: Array<String>?
    public var borderWidth: Number?
    public var tension: Number?
    public var pointRadius: Number?
    public var pointHoverRadius: Number?
    public var fill: Boolean?

    /** When false (Chart.js's default), null data points render as gaps in the line. */
    public var spanGaps: Boolean?
}

public external interface ChartOptions {
    public var responsive: Boolean?
    public var maintainAspectRatio: Boolean?
    public var aspectRatio: Number?
    public var devicePixelRatio: Number?
    public var scales: Scales?
    public var plugins: Plugins?
}

public external interface Plugins {
    public var legend: Legend?
}

public external interface Legend {
    public var display: Boolean?
}

public external interface Scales {
    public var x: Axis?
    public var y: Axis?
}

public external interface Axis {
    public var type: String?
    public var time: TimeOptions?
    public var title: AxisTitle?
    public var stacked: Boolean?
}

public external interface TimeOptions {
    public var unit: String?
}

public external interface AxisTitle {
    public var display: Boolean?
    public var text: String?
}

public external interface DatasetPoint {
    public var x: String?
    public var y: Number?
}

public fun <T : Any> new(init: T.() -> Unit): T {
    val result = js("({})")
    init.invoke(result)
    return result
}

